// Inline marks of a long text (decision 034): **bold**, *italic*, ~~strike~~, `code`, [links](…) and \-escapes — parsed to a small tree
// that the block editor and the reader's HTML both draw from. Everything is escaped first: a tag typed into a text is shown as text.

export type InlineNode =
  | { t: 'text'; v: string }
  | { t: 'b' | 'i' | 's'; c: InlineNode[] }
  | { t: 'code'; v: string }
  | { t: 'link'; href: string; c: InlineNode[] };

/** Characters a plain text escapes with «\» so they are not read as marks. */
const ESCAPABLE = /[\\*`[\]~]/;

export const escHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Plain text as inline markdown: the marks' characters get a «\». A «\» itself only where the parser would read it as an escape —
 * before such a character or at the end of a piece — so `C:\Users` stays as it was written.
 */
export const escInline = (s: string) => s.replace(/\\(?=[\\*`[\]~]|$)|[*`[\]~]/g, '\\$&');

/** The address of a link to a record of the account: `/r/<id>`. */
export const RECORD_HREF = /^\/r\/([0-9a-f-]{36})$/;

/**
 * The end of a mark that opens at `from`: the next unescaped `close` outside a code span, not right after the opening. In a run of
 * stars the mark closes at its end: `**b *c***` is a bold holding an italic.
 */
function closing(s: string, from: number, close: string): number {
  for (let j = from; j <= s.length - close.length; j++) {
    if (s[j] === '\\') { j++; continue; }
    if (s[j] === '`' && close !== '](') { const e = s.indexOf('`', j + 1); if (e > j) { j = e; continue; } }
    if (s.startsWith(close, j) && j > from) {
      if (close[0] === '*') { let k = j; while (s[k + close.length] === '*') k++; return k; }
      return j;
    }
  }
  return -1;
}

export function parseInline(s: string): InlineNode[] {
  const out: InlineNode[] = [];
  let text = '';
  const flush = () => { if (text) { out.push({ t: 'text', v: text }); text = ''; } };
  for (let i = 0; i < s.length;) {
    const ch = s[i]!;
    if (ch === '\\' && i + 1 < s.length && ESCAPABLE.test(s[i + 1]!)) { text += s[i + 1]; i += 2; continue; }
    if (ch === '`') {
      const end = s.indexOf('`', i + 1);
      if (end > i + 1) { flush(); out.push({ t: 'code', v: s.slice(i + 1, end) }); i = end + 1; continue; }
    }
    const pair = s.startsWith('**', i) ? '**' : s.startsWith('~~', i) ? '~~' : ch === '*' ? '*' : null;
    if (pair) {
      const end = closing(s, i + pair.length, pair);
      if (end > 0) {
        flush(); out.push({ t: pair === '**' ? 'b' : pair === '~~' ? 's' : 'i', c: parseInline(s.slice(i + pair.length, end)) }); i = end + pair.length; continue;
      }
    }
    if (ch === '[') {
      const mid = closing(s, i + 1, '](');
      const end = mid > 0 ? s.indexOf(')', mid + 2) : -1;
      if (end > 0 && !/\s/.test(s.slice(mid + 2, end))) { flush(); out.push({ t: 'link', href: s.slice(mid + 2, end), c: parseInline(s.slice(i + 1, mid)) }); i = end + 1; continue; }
    }
    text += ch; i++;
  }
  flush();
  return out;
}

/** Inline nodes back to markdown. */
export function inlineToMarkdown(nodes: InlineNode[]): string {
  return nodes.map((n) => {
    switch (n.t) {
      case 'text': return escInline(n.v);
      case 'code': return `\`${n.v.replace(/`/g, '')}\``;
      case 'b': return `**${inlineToMarkdown(n.c)}**`;
      case 'i': return `*${inlineToMarkdown(n.c)}*`;
      case 's': return `~~${inlineToMarkdown(n.c)}~~`;
      case 'link': return `[${inlineToMarkdown(n.c)}](${n.href})`;
    }
  }).join('');
}

/** The plain text of inline markdown (a title of a link, a search). */
export function inlineText(s: string): string {
  const walk = (ns: InlineNode[]): string => ns.map((n) => (n.t === 'text' || n.t === 'code' ? n.v : walk(n.c))).join('');
  return walk(parseInline(s));
}

/** Where links lead: `record` gives the address of a record link (null — the title only, e.g. on the public site). */
export interface LinkPolicy { record?: (id: string) => string | null }

/** Only http(s), mail and the account's own record links are links; «javascript:» and the like stay text. */
function safeHref(href: string, policy: LinkPolicy): string | null {
  const rec = RECORD_HREF.exec(href);
  if (rec) return policy.record ? policy.record(rec[1]!) : href;
  return /^(https?:\/\/|mailto:|#)/i.test(href) ? href : null;
}

export function inlineToHtml(s: string, policy: LinkPolicy = {}): string {
  const walk = (ns: InlineNode[]): string => ns.map((n) => {
    switch (n.t) {
      case 'text': return escHtml(n.v).replace(/\n/g, '<br>');
      case 'code': return `<code>${escHtml(n.v)}</code>`;
      case 'b': return `<strong>${walk(n.c)}</strong>`;
      case 'i': return `<em>${walk(n.c)}</em>`;
      case 's': return `<s>${walk(n.c)}</s>`;
      case 'link': {
        const href = safeHref(n.href, policy);
        const rec = RECORD_HREF.exec(n.href);
        if (!href) return walk(n.c);
        return rec ? `<a href="${escHtml(href)}" data-record="${rec[1]}">${walk(n.c)}</a>` : `<a href="${escHtml(href)}" target="_blank" rel="noreferrer">${walk(n.c)}</a>`;
      }
    }
  }).join('');
  return walk(parseInline(s));
}
